// API Configuration
export const API_CONFIG = {
  // Hardcoded to use real backend (not mocks)
  USE_MOCKS: false,
  
  // Backend API base URL
  BASE_URL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080',
  
  // API endpoints
  ENDPOINTS: {
    USERS_ME: '/users/me',
    AUTH_USER: '/auth/user',
    AUTH_USER_INFO: '/auth/user-info',
    META_EVENT: '/analytics/meta-event',
    TRACKS: '/tracks',
    TRACK_BY_ID: (id: string) => `/tracks/${id}`,
    UPLOAD_AUDIO: (trackId: string) => `/tracks/${trackId}/audio`,
    UPLOAD_VIDEO: (trackId: string) => `/tracks/${trackId}/video`,
    UPLOAD_PROMO: (trackId: string) => `/tracks/${trackId}/promo`,
    UPLOAD_ARTWORK: (trackId: string) => `/tracks/${trackId}/artwork`,
    UPLOAD_FOOTAGE: (trackId: string) => `/tracks/${trackId}/footages`,
    UPDATE_VIDEO_SOCIAL_LINKS: (trackId: string, fileId: string) => `/tracks/${trackId}/video/${fileId}/socialLinks`,
    DELETE_AUDIO: (trackId: string, fileId: string) => `/tracks/${encodeURIComponent(trackId)}/audio/${encodeURIComponent(fileId)}`,
    AUDIO_PREVIEW: (trackId: string, fileId: string) => {
      const encodedTrack = encodeURIComponent(trackId);
      const encodedFile = encodeURIComponent(fileId);
      if (fileId.includes('/')) {
        return `/tracks/${encodedTrack}/audio/preview?fileId=${encodedFile}`;
      }
      return `/tracks/${encodedTrack}/audio/${encodedFile}/preview`;
    },
    DELETE_VIDEO: (trackId: string, fileId: string) => `/tracks/${trackId}/video/${fileId}`,
    DELETE_FOOTAGE: (trackId: string, fileId: string) => `/tracks/${trackId}/footages/${fileId}`,
    DELETE_PROMO: (trackId: string, fileId: string) => `/tracks/${trackId}/promo/${fileId}`,
    DELETE_ARTWORK: (trackId: string, fileId: string) => `/tracks/${trackId}/artwork/${fileId}`,
    STRIPE_CHECKOUT: '/stripe/create-checkout-session',
    PROMOCODE_REDEEM: '/promocode/redeem',
    GDRIVE_OAUTH_START: '/gdrive/oauth/start',
    GDRIVE_OAUTH_CALLBACK: '/gdrive/oauth/callback',
    GDRIVE_SYNC: '/gdrive/sync',
    STORAGE_SYNC: '/storage/sync',
    TRACK_METADATA: (id: string) => `/tracks/${id}/metadata`,
    IMPORT_PROMO_IMAGES: (trackId: string) => `/tracks/${trackId}/promo/import`,
    TRACK_SHARES: (trackId: string) => `/tracks/${trackId}/shares`,
    TRACK_SHARE_COLLABORATOR: (trackId: string, collaboratorUserId: string) => `/tracks/${trackId}/shares/${collaboratorUserId}`,
    TRACK_SYNC_COLLABORATION: (trackId: string) => `/tracks/${trackId}/sync-collaboration`,
    UNCOLLABORATE: (trackId: string) => `/tracks/${trackId}/uncollaborate`,
    SHARED_WITH_ME: '/tracks/shared-with-me',
    USER_SEARCH: '/users/search',
    AUDIO_COMMENTS: (trackId: string, fileId: string) => {
      const encodedTrack = encodeURIComponent(trackId);
      const encodedFile = encodeURIComponent(fileId);
      if (fileId.includes('/')) {
        return `/tracks/${encodedTrack}/audio/comments?fileId=${encodedFile}`;
      }
      return `/tracks/${encodedTrack}/audio/${encodedFile}/comments`;
    },
    AUDIO_COMMENT_THREAD: (trackId: string, fileId: string, threadId: string) =>
      `/tracks/${encodeURIComponent(trackId)}/audio/${encodeURIComponent(fileId)}/comments/${encodeURIComponent(threadId)}`,
    AUDIO_COMMENT_REPLIES: (trackId: string, fileId: string, threadId: string) =>
      `/tracks/${encodeURIComponent(trackId)}/audio/${encodeURIComponent(fileId)}/comments/${encodeURIComponent(threadId)}/replies`,
    AUDIO_COMMENT_REPLY: (trackId: string, fileId: string, threadId: string, replyId: string) =>
      `/tracks/${encodeURIComponent(trackId)}/audio/${encodeURIComponent(fileId)}/comments/${encodeURIComponent(threadId)}/replies/${encodeURIComponent(replyId)}`,
    AUDIO_COMMENT_RESOLVE: (trackId: string, fileId: string, threadId: string) =>
      `/tracks/${encodeURIComponent(trackId)}/audio/${encodeURIComponent(fileId)}/comments/${encodeURIComponent(threadId)}:resolve`,
    STREAM_AUDIO: (trackId: string, fileId: string) => trackFileEndpoint(trackId, 'audio', fileId, 'stream'),
    PREVIEW_ARTWORK: (trackId: string, fileId: string) => trackFileEndpoint(trackId, 'artwork', fileId, 'preview'),
    PREVIEW_PROMO: (trackId: string, fileId: string) => trackFileEndpoint(trackId, 'promo', fileId, 'preview'),
    STREAM_VIDEO: (trackId: string, fileId: string) => trackFileEndpoint(trackId, 'video', fileId, 'stream'),
    STREAM_FOOTAGE: (trackId: string, fileId: string) => trackFileEndpoint(trackId, 'footage', fileId, 'stream'),
    FILE_ACCESS: (trackId: string, fileId: string, category: 'audio' | 'artwork' | 'promo' | 'video' | 'footage') => {
      const encodedTrack = encodeURIComponent(trackId);
      const encodedFile = encodeURIComponent(fileId);
      const encodedCategory = encodeURIComponent(category);
      return `/tracks/${encodedTrack}/files/access?fileId=${encodedFile}&category=${encodedCategory}`;
    },
  },
  
  // Request timeout
  TIMEOUT: 30000, // 30 seconds
  
  // File upload settings
  UPLOAD: {
    MAX_FILE_SIZE: 2 * 1024 * 1024 * 1024, // 2GB
    ALLOWED_AUDIO_TYPES: ['audio/mpeg', 'audio/wav', 'audio/mp3', 'audio/aac', 'audio/flac'],
    ALLOWED_VIDEO_TYPES: ['video/mp4', 'video/mov', 'video/avi', 'video/mkv'],
    ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  },

  GET_ARTWORK: (trackId: string) => `/tracks/${trackId}/artwork`,
  DELETE_ARTWORK: (trackId: string, fileId: string) => `/tracks/${trackId}/artwork/${fileId}`
};

// Helper to check if we should use mocks
export const shouldUseMocks = (): boolean => {
  return API_CONFIG.USE_MOCKS;
};

// Helper function to build full API URLs
export const getApiUrl = (endpoint: string): string => {
  return `${API_CONFIG.BASE_URL}${endpoint}`;
};

function trackFileEndpoint(
  trackId: string,
  category: 'audio' | 'artwork' | 'promo' | 'video' | 'footage',
  fileId: string,
  action: 'stream' | 'preview',
): string {
  const encodedTrack = encodeURIComponent(trackId);
  const encodedFile = encodeURIComponent(fileId);
  if (fileId.includes('/')) {
    const pathCategory = category === 'footage' ? 'footages' : category;
    return `/tracks/${encodedTrack}/${pathCategory}/${action}?fileId=${encodedFile}`;
  }
  if (category === 'audio') {
    return `/tracks/${encodedTrack}/audio/${encodedFile}/stream`;
  }
  if (category === 'video') {
    return `/tracks/${encodedTrack}/video/${encodedFile}/stream`;
  }
  if (category === 'footage') {
    return `/tracks/${encodedTrack}/footages/${encodedFile}/stream`;
  }
  return `/tracks/${encodedTrack}/${category}/${encodedFile}/preview`;
} 